import { createTheme, Theme } from "@mui/material/styles";
import { tokens } from "../styles/tokens";

/**
 * Custom MUI Theme — Uses Design Tokens
 * 
 * All values imported from tokens.ts (single source of truth).
 * To change colors/fonts, update tokens.ts and design-tokens.css
 */
export const customTheme = (outerTheme: Theme) =>
  createTheme({
    palette: {
      mode: "light",
      primary: {
        main: tokens.colors.accent,
        light: '#d4a853',
        dark: tokens.colors.accentHover,
        contrastText: tokens.colors.textInverse,
      },
      secondary: {
        main: tokens.colors.sage,
        light: '#6b7e6b',
        dark: '#354035',
        contrastText: tokens.colors.textInverse,
      },
      background: {
        default: tokens.colors.bgStart,
        paper: tokens.glass.whiteStrong,
      },
      text: {
        primary: tokens.colors.text,
        secondary: tokens.colors.textSecondary,
        disabled: tokens.colors.textMuted,
      },
      error: {
        main: tokens.colors.error,
      },
    },

    typography: {
      fontFamily: tokens.typography.fontSans,
      h1: {
        fontFamily: tokens.typography.fontSerif,
        fontWeight: tokens.typography.fontNormal,
        letterSpacing: "-0.02em",
      },
      h2: {
        fontFamily: tokens.typography.fontSerif,
        fontWeight: tokens.typography.fontNormal,
        letterSpacing: "-0.02em",
      },
      h3: {
        fontFamily: tokens.typography.fontSerif,
        fontWeight: tokens.typography.fontNormal,
      },
      h4: {
        fontFamily: tokens.typography.fontSerif,
        fontWeight: tokens.typography.fontNormal,
      },
      button: {
        textTransform: "none",
        fontWeight: tokens.typography.fontMedium,
      },
    },

    shape: {
      borderRadius: tokens.radiusPx.md,
    },

    components: {
      // Paper — Glass effect
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            backgroundColor: tokens.glass.whiteStrong,
            backdropFilter: `blur(20px)`,
            WebkitBackdropFilter: `blur(20px)`,
            border: `1px solid ${tokens.glass.borderSubtle}`,
          },
        },
      },

      // AppBar — Glass header
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            backdropFilter: `blur(${tokens.glass.blur}) saturate(180%)`,
            WebkitBackdropFilter: `blur(${tokens.glass.blur}) saturate(180%)`,
            borderBottom: `1px solid ${tokens.glass.borderSubtle}`,
            boxShadow: tokens.shadows.sm,
            color: tokens.colors.text,
          },
        },
      },

      // Button — Refined styling
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: tokens.radiusPx.sm,
            padding: "8px 20px",
            fontWeight: tokens.typography.fontMedium,
            boxShadow: "none",
            "&:hover": {
              boxShadow: tokens.shadows.md,
            },
          },
          containedPrimary: {
            background: tokens.colors.accent,
            "&:hover": {
              background: tokens.colors.accentHover,
            },
          },
        },
      },

      // TextField — Glass input style
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              backgroundColor: tokens.glass.whiteStrong,
              backdropFilter: "blur(12px)",
              borderRadius: tokens.radiusPx.md,
              "& fieldset": {
                borderColor: tokens.glass.borderSubtle,
              },
              "&:hover fieldset": {
                borderColor: tokens.colors.accentSoft,
              },
              "&.Mui-focused fieldset": {
                borderColor: tokens.colors.accent,
                borderWidth: 1,
              },
            },
            "& .MuiInputLabel-root": {
              color: tokens.colors.textSecondary,
            },
            "& .MuiInputLabel-root.Mui-focused": {
              color: tokens.colors.accent,
            },
          },
        },
      },

      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: tokens.glass.whiteStrong,
            backdropFilter: "blur(12px)",
            borderRadius: tokens.radiusPx.md,
          },
          notchedOutline: {
            borderColor: tokens.glass.borderSubtle,
            "& legend": {
              display: "none",
            },
          },
        },
      },

      MuiInputLabel: {
        styleOverrides: {
          root: {
            position: "static",
            transform: "none",
            maxWidth: "none",
            marginBottom: 4,
            fontSize: 12,
            fontWeight: 500,
            color: tokens.colors.textSecondary,
            "&.Mui-focused": {
              color: tokens.colors.accent,
            },
            "&.MuiInputLabel-shrink": {
              transform: "none",
            },
          },
        },
      },

      // Drawer — Glass sidebar
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: "rgba(248, 248, 247, 0.95)",
            backdropFilter: `blur(${tokens.glass.blur})`,
            WebkitBackdropFilter: `blur(${tokens.glass.blur})`,
            borderRight: `1px solid ${tokens.glass.borderSubtle}`,
          },
        },
      },

      // Menu — Glass dropdown
      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            border: `1px solid ${tokens.glass.borderSubtle}`,
            boxShadow: tokens.shadows.lg,
            borderRadius: tokens.radiusPx.md,
          },
        },
      },

      MuiMenuItem: {
        styleOverrides: {
          root: {
            borderRadius: tokens.radiusPx.sm,
            margin: "2px 8px",
            "&:hover": {
              backgroundColor: tokens.colors.accentSoft,
            },
          },
        },
      },

      // IconButton — Subtle hover
      MuiIconButton: {
        styleOverrides: {
          root: {
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.04)",
            },
          },
        },
      },

      // Tooltip — Refined
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: "rgba(26, 26, 26, 0.9)",
            backdropFilter: "blur(8px)",
            borderRadius: 6,
            fontSize: tokens.typography.textSm,
            padding: "6px 12px",
          },
        },
      },

      // Divider — Subtle
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: tokens.glass.borderSubtle,
          },
        },
      },
    },
  });

/**
 * Dark MUI Theme — Uses Dark Design Tokens
 */
export const darkTheme = (outerTheme: Theme) =>
  createTheme({
    palette: {
      mode: "dark",
      primary: {
        main: tokens.colors.accent,
        light: '#d4a853',
        dark: tokens.colors.accentHover,
        contrastText: tokens.colors.textInverse,
      },
      secondary: {
        main: tokens.colors.sage,
        light: '#6b7e6b',
        dark: '#354035',
        contrastText: tokens.colors.text,
      },
      background: {
        default: tokens.darkColors.bgStart,
        paper: tokens.darkColors.surface,
      },
      text: {
        primary: tokens.darkColors.text,
        secondary: tokens.darkColors.textSecondary,
        disabled: tokens.darkColors.textMuted,
      },
      error: {
        main: tokens.colors.error,
      },
    },

    typography: {
      fontFamily: tokens.typography.fontSans,
      h1: {
        fontFamily: tokens.typography.fontSerif,
        fontWeight: tokens.typography.fontNormal,
        letterSpacing: "-0.02em",
      },
      h2: {
        fontFamily: tokens.typography.fontSerif,
        fontWeight: tokens.typography.fontNormal,
        letterSpacing: "-0.02em",
      },
      h3: {
        fontFamily: tokens.typography.fontSerif,
        fontWeight: tokens.typography.fontNormal,
      },
      h4: {
        fontFamily: tokens.typography.fontSerif,
        fontWeight: tokens.typography.fontNormal,
      },
      button: {
        textTransform: "none",
        fontWeight: tokens.typography.fontMedium,
      },
    },

    shape: {
      borderRadius: tokens.radiusPx.md,
    },

    components: {
      // Paper — Dark glass effect
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            backgroundColor: tokens.darkColors.surface,
            backdropFilter: `blur(20px)`,
            WebkitBackdropFilter: `blur(20px)`,
            border: `1px solid ${tokens.darkColors.glassBorder}`,
          },
        },
      },

      // AppBar — Dark glass header
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: "rgba(28, 37, 54, 0.9)",
            backdropFilter: `blur(${tokens.glass.blur}) saturate(180%)`,
            WebkitBackdropFilter: `blur(${tokens.glass.blur}) saturate(180%)`,
            borderBottom: `1px solid ${tokens.darkColors.glassBorder}`,
            boxShadow: tokens.shadows.sm,
            color: tokens.darkColors.text,
          },
        },
      },

      // Button — Refined styling
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: tokens.radiusPx.sm,
            padding: "8px 20px",
            fontWeight: tokens.typography.fontMedium,
            boxShadow: "none",
            "&:hover": {
              boxShadow: tokens.shadows.md,
            },
          },
          containedPrimary: {
            background: tokens.colors.accent,
            "&:hover": {
              background: tokens.colors.accentHover,
            },
          },
        },
      },

      // TextField — Dark glass input style
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              backgroundColor: tokens.darkColors.glassWhiteStrong,
              backdropFilter: "blur(12px)",
              borderRadius: tokens.radiusPx.md,
              "& fieldset": {
                borderColor: tokens.darkColors.glassBorder,
              },
              "&:hover fieldset": {
                borderColor: tokens.darkColors.glassBorderLight,
              },
              "&.Mui-focused fieldset": {
                borderColor: tokens.colors.accent,
                borderWidth: 1,
              },
            },
            "& .MuiInputLabel-root": {
              color: tokens.darkColors.textSecondary,
            },
            "& .MuiInputLabel-root.Mui-focused": {
              color: tokens.colors.accent,
            },
            "& .MuiInputBase-input": {
              color: tokens.darkColors.text,
            },
          },
        },
      },

      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: tokens.darkColors.glassWhiteStrong,
            backdropFilter: "blur(12px)",
            borderRadius: tokens.radiusPx.md,
          },
          notchedOutline: {
            borderColor: tokens.darkColors.glassBorder,
            // Labels live above the input now (see MuiInputLabel below), so collapse
            // the notched gap — the border should be a clean unbroken pill.
            "& legend": {
              display: "none",
            },
          },
        },
      },

      MuiInputLabel: {
        styleOverrides: {
          root: {
            // Render labels statically ABOVE the input box rather than as floating
            // labels that cross the border line. Cleaner, more readable in dark mode,
            // and avoids the notched-outline contrast issues entirely.
            position: "static",
            transform: "none",
            maxWidth: "none",
            marginBottom: 4,
            fontSize: 12,
            fontWeight: 500,
            color: tokens.darkColors.textSecondary,
            "&.Mui-focused": {
              color: tokens.colors.accent,
            },
            "&.MuiInputLabel-shrink": {
              transform: "none",
            },
          },
        },
      },

      // Drawer — Dark glass sidebar
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: "rgba(28, 37, 54, 0.95)",
            backdropFilter: `blur(${tokens.glass.blur})`,
            WebkitBackdropFilter: `blur(${tokens.glass.blur})`,
            borderRight: `1px solid ${tokens.darkColors.glassBorder}`,
          },
        },
      },

      // Menu — Dark glass dropdown
      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundColor: "rgba(47, 59, 82, 0.95)",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            border: `1px solid ${tokens.darkColors.glassBorder}`,
            boxShadow: tokens.shadows.lg,
            borderRadius: tokens.radiusPx.md,
          },
        },
      },

      MuiMenuItem: {
        styleOverrides: {
          root: {
            borderRadius: tokens.radiusPx.sm,
            margin: "2px 8px",
            "&:hover": {
              backgroundColor: tokens.darkColors.glassWhiteStrong,
            },
          },
        },
      },

      // IconButton — Subtle hover
      MuiIconButton: {
        styleOverrides: {
          root: {
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.08)",
            },
          },
        },
      },

      // Tooltip — Refined
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: "rgba(0, 0, 0, 0.85)",
            backdropFilter: "blur(8px)",
            borderRadius: 6,
            fontSize: tokens.typography.textSm,
            padding: "6px 12px",
          },
        },
      },

      // Divider — Subtle
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: tokens.darkColors.glassBorder,
          },
        },
      },
    },
  });

